import { useTrackingUuid } from '@sortlist-frontend/tracking';
import { useRouter } from 'next/router';
import { useReportWebVitals } from 'next/web-vitals';
import { useCallback, useEffect, useRef } from 'react';

import { sendWebVitalsReport } from '../sendWebVitalsReport';
import { CONNECTION_TYPES, ConnectionType, NAVIGATION_TYPES, NavigationType, PageLoadMetrics } from '../types';

export interface ReportedMetric {
  name: string;
  value: number;
  navigationType?: string;
}

const DURATION_METRICS = {
  TTFB: 'ttfb',
  FCP: 'fcp',
  LCP: 'lcp',
  INP: 'inp',
} as const;

const isDurationMetric = (name: string): name is keyof typeof DURATION_METRICS =>
  Object.prototype.hasOwnProperty.call(DURATION_METRICS, name);

const isNavigationType = (value: string | undefined): value is NavigationType =>
  NAVIGATION_TYPES.some((navigationType) => navigationType === value);

const isConnectionType = (value: unknown): value is ConnectionType =>
  CONNECTION_TYPES.some((connectionType) => connectionType === value);

const EMPTY_METRICS: PageLoadMetrics = {
  ttfb: null,
  fcp: null,
  lcp: null,
  inp: null,
  cls: null,
  navigationType: null,
};

const readConnectionType = (): ConnectionType | null => {
  const connection: unknown = 'connection' in navigator ? navigator.connection : null;
  if (typeof connection !== 'object' || connection == null || !('effectiveType' in connection)) return null;
  return isConnectionType(connection.effectiveType) ? connection.effectiveType : null;
};

const recordMetric = (metrics: PageLoadMetrics, metric: ReportedMetric): PageLoadMetrics => {
  const navigationType =
    metrics.navigationType ?? (isNavigationType(metric.navigationType) ? metric.navigationType : null);

  if (metric.name === 'CLS') {
    return { ...metrics, navigationType, cls: Math.round(metric.value * 10_000) / 10_000 };
  }
  if (isDurationMetric(metric.name)) {
    return { ...metrics, navigationType, [DURATION_METRICS[metric.name]]: Math.round(metric.value) };
  }
  return metrics;
};

const hasAnyMetric = (metrics: PageLoadMetrics) =>
  metrics.ttfb != null || metrics.fcp != null || metrics.lcp != null || metrics.inp != null || metrics.cls != null;

export const useLogWebVitals = () => {
  const { pathname } = useRouter();
  const loadedRoute = useRef(pathname);
  const metrics = useRef<PageLoadMetrics>(EMPTY_METRICS);
  const isSent = useRef(false);
  const { sessionUuid, userUuid } = useTrackingUuid();
  const trackingIds = useRef({ sessionUuid, userUuid });
  trackingIds.current = { sessionUuid, userUuid };

  const handleMetric = useCallback((metric: ReportedMetric) => {
    metrics.current = recordMetric(metrics.current, metric);
  }, []);

  useReportWebVitals(handleMetric);

  useEffect(() => {
    const loadedPath = window.location.pathname;

    const send = () => {
      if (isSent.current || !hasAnyMetric(metrics.current)) return;
      isSent.current = true;
      void sendWebVitalsReport({
        kind: 'pageLoad',
        route: loadedRoute.current,
        path: loadedPath,
        ...trackingIds.current,
        ...metrics.current,
        connectionType: readConnectionType(),
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') send();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', send);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', send);
    };
  }, []);
};
